import axios from 'axios';


const ENV_HOST = process.env.REACT_APP_SEMANTIC_SEARCH_API_HOST;
const LOCALHOST_FALLBACK = 'http://localhost:8000';

const BASE_URL = ENV_HOST || LOCALHOST_FALLBACK;
const API_BASE_URL = `${BASE_URL}/api/v1`;

// OpenSearch API client
const opensearchClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Types for OpenSearch API responses
export interface OpenSearchDocument {
  id: string;
  content: {
    video_id?: string;
    channel_name?: string;
    channel_url?: string;
    video_title?: string;
    video_url?: string;
    video_description?: string;
    transcription?: string;
    searchTranscription?: string;
    summary?: string;
    searchSummary?: string;
    published_date?: string;
    duration?: number;
    views?: number;
    likes?: number;
    comments?: number;
    thumbnail_url?: string;
    platform?: string;
    searchKeywords?: string[];
    searchEntities?: string[];
    sentimentPositive?: number;
    sentimentNegative?: number;
    sentimentNeutral?: number;
    transcription_embedding?: number[];
    summary_embedding?: number[];
    rawData?: {
      _id?: string;
      metaData?: {
        id?: string;
        creatorId?: string;
        contentType?: string;
        duration?: string;
        title?: string;
        description?: string;
        date?: string;
        thumbnail?: string;
        url?: string;
        platform?: string;
        text?: string;
        segments?: number;
        cdnVideoPath?: string;
        cdnThumbnailPath?: string;
        isCollection?: boolean;
        collectionId?: string;
        hashtags?: string[];
        links?: string[];
        keywords?: string[];
        mentions?: string[];
        stats?: {
          views?: number;
          likes?: number;
          dislikes?: number;
          comments?: number;
        };
        sentiment?: {
          positive?: string;
          negative?: string;
          neutral?: string;
        };
        replayTimeline?: any;
        music?: any;
        advertising?: any;
        cdnPath?: string;
        creatorUrl?: string;
        creatorName?: string[];
      };
      transcription?: {
        status?: string;
        lang?: string;
        baseTranscription?: {
          segments?: Array<{
            id?: number;
            start?: number;
            end?: number;
            text?: string;
          }>;
        };
        englishTranscription?: {
          text?: string;
          segments?: any[];
        };
      };
      textAnalytics?: any;
      internalStats?: any[];
      status?: number;
      errors?: any;
      createdAt?: string;
      wordCount?: number;
      creatorId?: string;
      creatorData?: Array<{
        _id?: string;
        profileName?: string;
        personName?: string;
        companyName?: string;
        platforms?: {
          Youtube?: {
            platformId?: string;
            username?: string;
            fullname?: string;
            url?: string;
            profileImageURL?: string;
            description?: string;
            about?: string;
            connections?: {
              subscribers?: string;
            };
            posts?: {
              videos?: string;
              shorts?: string;
              playlists?: string;
            };
            stats?: {
              likes?: string;
              comments?: string;
              views?: string;
            };
          };
          Instagram?: {
            platformId?: string;
            username?: string;
            fullname?: string;
            url?: string;
            profileImageURL?: string;
            description?: string;
            about?: string;
            connections?: {
              subscribers?: string;
            };
            posts?: {
              videos?: string;
              shorts?: string;
              playlists?: string;
            };
            stats?: {
              likes?: string;
              comments?: string;
              views?: string;
            };
          };
          Tiktok?: {
            platformId?: string;
            username?: string;
            fullname?: string;
            url?: string;
            profileImageURL?: string;
            description?: string;
            about?: string;
            connections?: {
              subscribers?: string;
            };
            posts?: {
              videos?: string;
              shorts?: string;
              playlists?: string;
            };
            stats?: {
              likes?: string;
              comments?: string;
              views?: string;
            };
          };
        };
      }>;
    };
    [key: string]: any;
  };
  score?: number;
}

export interface OpenSearchSearchResponse {
  total: number;
  results: OpenSearchDocument[];
  took: number;
  index_counts?: Record<string, number>;
}

export interface OpenSearchSemanticSearchParams {
  q: string;
  size?: number;
  from?: number;
  index_pattern?: string;
  use_gpu_embedding?: boolean;
  search_field?: 'transcription' | 'summary';
}

export interface OpenSearchHybridSearchParams {
  semantic_query: string;
  keyword_query?: string;
  entity_query?: string;
  keyword_weight?: number;
  semantic_weight?: number;
  size?: number;
  from?: number;
  fuzziness?: string;
  index_pattern?: string;
  use_gpu_embedding?: boolean;
  from_date?: string;
  to_date?: string;
  analysis_from_date?: string;
  analysis_to_date?: string;
  language?: string;
  platform?: string;
  reach?: string;
  sentiment?: string;
  entity_logic?: string;
}

export interface OpenSearchNaturalLanguageParams {
  query: string;
  size?: number;
  from?: number;
  index_pattern?: string;
  use_gpu_embedding?: boolean;
}

export interface ParsedQueryResponse {
  date_range?: {
    from_date: string;
    to_date: string;
  };
  analysis_date_range?: {
    from_date: string;
    to_date: string;
  };
  semantic_query: string;
  keyword_query?: string;
  entity_query?: string;
  language?: string;
  platform?: string;
  reach?: string;
  sentiment?: string;
}

// API functions
export const opensearchApi = {
  // Semantic search
  async semanticSearch(params: OpenSearchSemanticSearchParams): Promise<OpenSearchSearchResponse> {
    try {
      console.log('🔍 OpenSearch Semantic Search Request:', {
        query: params.q,
        size: params.size || 10,
        index_pattern: params.index_pattern || '*-transcripts',
        search_field: params.search_field || 'transcription',
      });

      const response = await opensearchClient.get('/search/semantic', {
        params: {
          q: params.q,
          size: params.size || 10,
          from: params.from || 0,
          index_pattern: params.index_pattern || '*-transcripts',
          use_gpu_embedding: params.use_gpu_embedding !== false, // default to true
          search_field: params.search_field || 'transcription',
        },
      });
      
      console.log('📊 OpenSearch API Response:', {
        total: response.data.total,
        took: response.data.took,
        index_counts: response.data.index_counts,
        results_count: response.data.results?.length || 0,
      });

      // Log first 10 results with key fields
      if (response.data.results && response.data.results.length > 0) {
        console.log('🎯 First 10 Search Results:');
        response.data.results.slice(0, 10).forEach((result: any, index: number) => {
          console.log(`Result ${index + 1}:`, {
            id: result.id,
            video_id: result.content?.video_id,
            video_title: result.content?.video_title,
            channel_name: result.content?.channel_name,
            platform: result.content?.platform,
            published_date: result.content?.published_date,
            search_date: result.content?.searchDate,
            views: result.content?.statsViews || result.content?.views,
            likes: result.content?.statsLikes || result.content?.likes,
            comments: result.content?.statsComments || result.content?.comments,
            duration: result.content?.duration,
            transcription_preview: result.content?.searchTranscription?.substring(0, 100) + '...',
            summary_preview: result.content?.searchSummary?.substring(0, 100) + '...',
            search_keywords: result.content?.searchKeywords,
            search_entities: result.content?.searchEntities,
            search_hashtags: result.content?.searchHashtags,
            search_topics: result.content?.searchTopics,
            sentiment_positive: result.content?.sentimentPositive,
            sentiment_negative: result.content?.sentimentNegative,
            sentiment_neutral: result.content?.sentimentNeutral,
            creator_followers: result.content?.creatorFollowers,
            score: result.score,
          });
        });
      } else {
        console.log('❌ No results found');
      }
      
      return {
        total: response.data.total || response.data.results?.length || 0,
        results: response.data.results || [],
        took: response.data.took || 0,
        index_counts: response.data.index_counts,
      };
    } catch (error) {
      console.error('❌ Semantic search error:', error);
      throw new Error(`Semantic search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },


  // Natural language search
  async naturalLanguageSearch(params: OpenSearchNaturalLanguageParams): Promise<OpenSearchSearchResponse> {
    try {
      const response = await opensearchClient.post('/search/natural-language', {
        query: params.query,
      }, {
        params: {
          size: params.size || 10,
          from: params.from || 0,
          index_pattern: params.index_pattern || '*-transcripts',
          use_gpu_embedding: params.use_gpu_embedding !== false, // default to true
        },
      });
      
      return {
        total: response.data.total || response.data.results?.length || 0,
        results: response.data.results || [],
        took: response.data.took || 0,
        index_counts: response.data.index_counts,
      };
    } catch (error) {
      console.error('Natural language search error:', error);
      throw new Error(`Natural language search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      // Use the dedicated health endpoint at the root level
      const healthUrl = `${BASE_URL}/health`;
      const response = await axios.get(healthUrl, {
        timeout: 5000, // Shorter timeout for health check
        headers: {
          'Accept': 'application/json',
        },
      });
      return response.status === 200;
    } catch (error) {
      console.error('OpenSearch health check failed:', error);
      return false;
    }
  },

  // Get document counts
  async getDocumentCounts(): Promise<Record<string, number>> {
    try {
      const response = await opensearchClient.get('/documents/count');
      return response.data.counts || {};
    } catch (error) {
      console.error('Get document counts error:', error);
      throw new Error(`Failed to get document counts: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Generate index overview summary
  async generateIndexOverview(
    indexName: string, 
    maxWords: number = 100, 
    maxDocs: number = 1000
  ): Promise<{
    index_name: string;
    overview: string;
    summary_count: number;
    word_count: number;
    max_words_requested: number;
    service: string;
  }> {
    try {
      console.log(`📊 Generating index overview for: ${indexName}`);
      const response = await opensearchClient.post(`/index/${indexName}/summarize-overview`, null, {
        params: {
          max_words: maxWords,
          max_docs: maxDocs,
        },
      });
      
      console.log('✅ Index overview generated successfully:', {
        index: response.data.index_name,
        wordCount: response.data.word_count,
        summaryCount: response.data.summary_count,
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Index overview generation failed:', error);
      throw new Error(`Failed to generate index overview: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Generate summary from search results (for Results summary tab)
  async generateSearchResultsSummary(
    searchResults: any[],
    maxWords: number = 100
  ): Promise<{
    summary: string;
    result_count: number;
    word_count: number;
    max_words_requested: number;
    service: string;
  }> {
    try {
      console.log(`📊 Generating search results summary for ${searchResults.length} results`);
      
      // Extract summaries from search results
      // The search results come as OpenSearch documents, we need to extract from the correct path
      const summaries = searchResults
        .map(result => {
          // Try multiple possible paths for summary
          return result.summary || 
                 result._source?.summary || 
                 result._source?.searchSummary ||
                 result.content?.summary ||
                 result.content?.searchSummary ||
                 result.rawData?.metaData?.summary;
        })
        .filter(summary => summary && summary.trim().length > 0);
      
      if (summaries.length === 0) {
        return {
          summary: 'No summaries available in the search results.',
          result_count: 0,
          word_count: 0,
          max_words_requested: maxWords,
          service: 'opensearch'
        };
      }
      
      // Join all summaries with a separator
      const combinedSummaries = summaries.join('\n\n');
      
      // Call the real backend endpoint
      const response = await opensearchClient.post('/search/summarize-results', {
        summaries: combinedSummaries,
        max_words: maxWords,
        result_count: summaries.length,
      });
      
      console.log('✅ Search results summary generated successfully:', {
        resultCount: response.data.result_count,
        wordCount: response.data.word_count,
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Search results summary generation failed:', error);
      throw new Error(`Failed to generate search results summary: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Generate Q&A answer from search results (for Answer tab)
  async generateSearchResultsQaAnswer(
    searchResults: any[],
    question: string,
    maxWords: number = 150
  ): Promise<{
    answer: string;
    result_count: number;
    question: string;
    service: string;
  }> {
    try {
      console.log(`🤖 Generating Q&A answer for question: "${question}" using ${searchResults.length} results`);
      
      // Extract summaries from search results
      const summaries = searchResults
        .map(result => {
          // Try multiple possible paths for summary
          return result.summary || 
                 result._source?.summary || 
                 result._source?.searchSummary ||
                 result.content?.summary ||
                 result.content?.searchSummary ||
                 result.rawData?.metaData?.summary;
        })
        .filter(summary => summary && summary.trim().length > 0);
      
      if (summaries.length === 0) {
        return {
          answer: 'No summaries available in the search results to answer your question.',
          result_count: 0,
          question: question,
          service: 'opensearch'
        };
      }
      
      // Join all summaries with a separator
      const combinedSummaries = summaries.join('\n\n');
      
      // Call the real backend endpoint
      const response = await opensearchClient.post('/search/qa-answer', {
        question: question,
        summaries: combinedSummaries,
        result_count: summaries.length,
        max_words: maxWords,
      });
      
      console.log('✅ Q&A answer generated successfully:', {
        question: response.data.question,
        resultCount: response.data.result_count,
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Q&A answer generation failed:', error);
      throw new Error(`Failed to generate Q&A answer: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Generate unlimited index overview summary with chunking
  async generateIndexOverviewUnlimited(
    indexName: string, 
    maxWords: number = 100, 
    chunkSize: number = 300
  ): Promise<{
    index_name: string;
    overview: string;
    summary_count: number;
    word_count: number;
    max_words_requested: number;
    chunks_processed: number;
    chunk_size: number;
    service: string;
  }> {
    try {
      console.log(`📊 Generating unlimited index overview for: ${indexName} with chunk size: ${chunkSize}`);
      const response = await opensearchClient.post(`/index/${indexName}/summarize-overview-unlimited`, null, {
        params: {
          max_words: maxWords,
          chunk_size: chunkSize,
        },
        timeout: 300000, // 5 minutes timeout for unlimited processing
      });
      
      console.log('✅ Unlimited index overview generated successfully:', {
        index: response.data.index_name,
        wordCount: response.data.word_count,
        summaryCount: response.data.summary_count,
        chunksProcessed: response.data.chunks_processed,
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Unlimited index overview generation failed:', error);
      throw new Error(`Failed to generate unlimited index overview: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Parse natural language query
  async parseQuery(query: string): Promise<ParsedQueryResponse> {
    try {
      console.log(`🔍 Parsing query: "${query}"`);
      const response = await opensearchClient.post('/search/parse-query', {
        query: query,
      });
      
      console.log('✅ Query parsed successfully:', {
        semanticQuery: response.data.semantic_query,
        keywordQuery: response.data.keyword_query,
        entityQuery: response.data.entity_query,
        dateRange: response.data.date_range,
        analysisDateRange: response.data.analysis_date_range,
        language: response.data.language,
        platform: response.data.platform,
        reach: response.data.reach,
        sentiment: response.data.sentiment,
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Query parsing failed:', error);
      throw new Error(`Failed to parse query: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Hybrid search
  async hybridSearch(params: OpenSearchHybridSearchParams): Promise<OpenSearchSearchResponse> {
    try {
      console.log(`🔍 Hybrid search with params:`, {
        semanticQuery: params.semantic_query,
        keywordQuery: params.keyword_query,
        entityQuery: params.entity_query,
        size: params.size,
        from: params.from,
        fromDate: params.from_date,
        toDate: params.to_date,
        analysisFromDate: params.analysis_from_date,
        analysisToDate: params.analysis_to_date,
        language: params.language,
        platform: params.platform,
        reach: params.reach,
        sentiment: params.sentiment,
        entityLogic: params.entity_logic,
        note: 'language, reach, sentiment now support comma-separated values',
      });
      
      const response = await opensearchClient.get('/search/hybrid', {
        params: {
          semantic_query: params.semantic_query,
          keyword_query: params.keyword_query || undefined,
          entity_query: params.entity_query || undefined,
          keyword_weight: params.keyword_weight || 0.5,
          semantic_weight: params.semantic_weight || 0.5,
          size: params.size || 10,
          from: params.from || 0,
          fuzziness: params.fuzziness || undefined,
          index_pattern: params.index_pattern || '*-transcripts',
          use_gpu_embedding: params.use_gpu_embedding !== false,
          from_date: params.from_date || undefined,
          to_date: params.to_date || undefined,
          analysis_from_date: params.analysis_from_date || undefined,
          analysis_to_date: params.analysis_to_date || undefined,
          language: params.language || undefined,
          platform: params.platform || undefined,
          reach: params.reach || undefined,
          sentiment: params.sentiment || undefined,
          entity_logic: params.entity_logic || undefined,
        },
      });
      
      console.log('✅ Hybrid search completed:', {
        total: response.data.total,
        resultsCount: response.data.results?.length,
        took: response.data.took,
      });
      
      return {
        total: response.data.total || response.data.results?.length || 0,
        results: response.data.results || [],
        took: response.data.took || 0,
        index_counts: response.data.index_counts,
      };
    } catch (error) {
      console.error('❌ Hybrid search failed:', error);
      throw new Error(`Failed to perform hybrid search: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get embedding status
  async getEmbeddingStatus(): Promise<any> {
    try {
      const response = await opensearchClient.get('/documents/embedding-status');
      return response.data.embedding_stats || {};
    } catch (error) {
      console.error('Get embedding status error:', error);
      throw new Error(`Failed to get embedding status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },
};

export default opensearchApi;
